/**
 * @fileoverview Security Section - "Built for security & compliance"
 *
 * Purpose: Reassure firms about Morgan's security and compliance posture.
 *
 * Layout:
 * - Two-column layout: heading + CTA on the left, a 2x2 grid of compact
 *   feature cells on the right.
 * - The 2x2 grid uses the same line-delineated treatment as the feature cards
 *   section (a single vertical + single horizontal divider line), with smaller
 *   cells. Each cell has an icon, heading, and supporting copy.
 *
 * Theming: uses the global semantic CSS variables so the section adapts to
 * light and dark mode (the marketing footer exposes a theme switcher).
 *
 * Used by: Homepage (index.tsx)
 */

import { Button } from "@/ui/primitives";
import { ArrowRight, Lock, Brain, KeyRound, UserCheck } from "lucide-react";
import { cn } from "@/utils/misc";

/** Security feature content. */
const SECURITY_CARDS = [
  {
    icon: Lock,
    title: "End-to-end encryption",
    description:
      "All data encrypted in transit (TLS 1.2+) & at rest; documents are never stored unprotected.",
  },
  {
    icon: UserCheck,
    title: "Secure compliance",
    description:
      "Morgan is SOC 2 Type 1 and ISO 27001 certified and independently audited.",
  },
  {
    icon: KeyRound,
    title: "Authentication & access",
    description:
      "Enterprise sign-in with Single Sign-On & multi-factor authentication.",
  },
  {
    icon: Brain,
    title: "No training on data",
    description:
      "None of our AI providers use your data to train their models.",
  },
] as const;

/**
 * Security section with a heading + CTA and a 2x2 grid of compact feature cells.
 */
export function SecuritySection() {
  return (
    <section className="relative w-full border-t border-primary/10 bg-background py-8 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 items-start gap-12 md:grid-cols-2 md:gap-16">
          {/* Left: heading + CTA */}
          <div>
            <h2 className="max-w-md text-3xl font-medium leading-[1.1] text-primary md:text-[42px]">
              Built for security &amp;
              <br />
              compliance
            </h2>
            <Button
              asChild
              variant="default"
              size="lg"
              expandIcon
              className="mt-8 gap-2 text-base"
            >
              <a
                href="https://trust.morgan.so/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Go to trust center
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>

          {/* Right: 2x2 grid delineated by divider lines */}
          <div className="grid grid-cols-1 sm:grid-cols-2">
            {SECURITY_CARDS.map((card, index) => {
              const isLeftCol = index % 2 === 0;
              const isTopRow = index < 2;
              const Icon = card.icon;

              return (
                <div
                  key={card.title}
                  className={cn(
                    "flex flex-col border-primary/10 p-6",
                    // Desktop: a single horizontal line under the top row only
                    // (no dividers between the vertically-stacked cells on mobile)
                    isTopRow && "sm:border-b",
                    // Desktop: top row aligns its icons with the heading top
                    isTopRow && "sm:pt-0",
                    // Desktop: single vertical line between the two columns
                    isLeftCol ? "sm:border-r sm:pr-6" : "sm:pl-6",
                  )}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold leading-snug text-primary">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/60">
                    {card.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
